import { msg } from "@lingui/macro";
import type { I18n } from "@lingui/core";

/**
 * Structural shape consumed by the label formatters. Both
 * `reconciliation_DebtorAccountDTO` and `billing_DebtorDisplayDTO` satisfy it,
 * so any list endpoint that wants the canonical
 * `Family Schulte (0420 Mia Schulte, ...)` label can return either DTO.
 */
export interface DebtorLabelInput {
  familyGroupId?: string;
  familySurname?: string;
  contactFirstname?: string;
  contactLastname?: string;
  displayName?: string;
  children?: Array<{
    childInternalId?: string;
    firstname?: string;
    lastname?: string;
  }>;
}

/**
 * Formats a debtor's children list for the "(0420 Mia Schulte, 0550 Leo Schulte)"
 * suffix shown in the Debtor Accounts table cell and detail slide-over.
 * Returns an empty string when the debtor has no children attached so callers
 * can conditionally render the surrounding parentheses.
 */
export function formatDebtorChildren(
  children: DebtorLabelInput["children"],
): string {
  if (!children || children.length === 0) return "";
  return children
    .map((c) =>
      [c.childInternalId, c.firstname, c.lastname].filter(Boolean).join(" "),
    )
    .join(", ");
}

/**
 * Returns the contact full name ("Martin Schulte") for an individual-contact
 * debtor, or empty string when the JOIN didn't resolve. Empty string lets
 * callers fall back to the stored displayName.
 */
export function debtorContactName(debtor: DebtorLabelInput): string {
  if (debtor.familyGroupId) return "";
  return [debtor.contactFirstname, debtor.contactLastname]
    .filter(Boolean)
    .join(" ");
}

/**
 * Plain-string lead label for a debtor — "Family Schulte" / "Familie Schulte"
 * for households, "Martin Schulte" for contact debtors, falling back to the
 * stored displayName when neither JOIN resolved (e.g. legacy rows). Used by
 * dialog titles and any place that interpolates the debtor name into a
 * translation template via `i18n._({ values: { name } })`.
 */
export function debtorLeadName(debtor: DebtorLabelInput, i18n: I18n): string {
  const familySurname = debtor.familyGroupId ? debtor.familySurname : undefined;
  if (familySurname) {
    return i18n._(
      msg({
        id: "reconciliation.debtorAccounts.label.family",
        message: "Family {familySurname}",
      }),
      { familySurname },
    );
  }
  const contactName = debtorContactName(debtor);
  if (contactName) return contactName;
  return debtor.displayName ?? "";
}

/**
 * Full debtor label: lead name followed by the children list in parens, e.g.
 * "Family Schulte (0420 Mia Schulte, 0550 Leo Schulte)". Returns just the
 * lead name when the debtor has no children attached.
 */
export function debtorLabel(debtor: DebtorLabelInput, i18n: I18n): string {
  const lead = debtorLeadName(debtor, i18n);
  const children = formatDebtorChildren(debtor.children);
  return children ? `${lead} (${children})` : lead;
}

/**
 * Default cap for a debtor label rendered into a table cell. A household with
 * several children produces labels long enough to blow out a column, so
 * callers render a truncated string and surface the full one on hover.
 */
export const MAX_DEBTOR_LABEL_CHARS = 60;

/**
 * Truncates a debtor label to `maxChars`, appending an ellipsis when it had to
 * cut. Callers that truncate MUST also expose the full label on hover (a
 * native `title`) — a silently clipped household name reads as a different
 * family.
 *
 * Set that `title` unconditionally rather than gating it on this function's
 * cap: table cells also carry a CSS `truncate`, which clips on column width at
 * a length no character count can predict, and that clipping is invisible to
 * JS. A redundant tooltip costs less than a name that reads as another family.
 */
export function truncateDebtorLabel(
  label: string,
  maxChars: number = MAX_DEBTOR_LABEL_CHARS,
): string {
  if (label.length <= maxChars) return label;
  return label.slice(0, maxChars - 1) + "…";
}

/** True when {@link truncateDebtorLabel} would shorten `label`. */
export function isDebtorLabelTruncated(
  label: string,
  maxChars: number = MAX_DEBTOR_LABEL_CHARS,
): boolean {
  return label.length > maxChars;
}

/**
 * Returns the React Router URL that opens the Reconciliation > Debtor Accounts
 * slide-over for the given debtor. Used by row links in the Workflow Inbox and
 * Dunning Cockpit. Browser back returns to the originating page.
 *
 * The Reconciliation page reads `tab` to switch tabs and `debtor` to auto-open
 * the slide-over (see pages/billing/ReconciliationPage.tsx).
 */
export function debtorDetailHref(debtorAccountId: string): string {
  return `/billing/reconciliation?tab=debtor-accounts&debtor=${encodeURIComponent(debtorAccountId)}`;
}
